import React, { useEffect, useMemo } from 'react';
import { demeterApi } from '../../../../../Apis/Apis';
import { DairyInsurancePriceModel, DataSeries, DemeterCommodity, DemeterEmailSubscriptionType } from '../../../../../Generated/Raven-Demeter';
import useMultipleApis from '../../../../Apis/Hooks/useMultipleApisHook';
import informationSvg from '../../../../Assets/Icons/info.svg';
import PageAccordion from '../../../../Components/Form/Buttons/Accordions/PageAccordion';
import Table from '../../../../Components/Tables/Table/Table';
import TableWrapper from '../../../../Components/Tables/TableWrapper/TableWrapper';
import tableCsvDownloadService from '../../../../Services/Download/DownloadService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from '../InsuranceQuoteEstimator.module.scss';
import CoverageLevelsActions from '../LivestockRevenueProtection/CoverageLevelsActions';
import {
    dairyPremiumInformationTableHeaders,
    DairyPremiumInformationTableRow,
    DairyRevenueProtectionCalculationInputs,
    DairySelectionTableRow,
} from './DairyRevenueProtectionDefinitions';

interface IDairyRevenueProtectionPremiumInformationProps {
    calculationInputs: DairyRevenueProtectionCalculationInputs;
    calculatedTableRows: DairySelectionTableRow[];
    accordionOpen: boolean;
}

const DairyRevenueProtectionPremiumInformation: React.FC<IDairyRevenueProtectionPremiumInformationProps> = (
    props: IDairyRevenueProtectionPremiumInformationProps,
) => {
    const [translations] = useLanguage();

    const includeTotalsRow = props.calculationInputs.classBasePercent && props.calculationInputs.componentBasedPercent;

    const [, refreshRunDairyInsurancePrices, runDairyInsurancePricesResponse] = useMultipleApis(() => {
        if (props.calculatedTableRows.length === 0) {
            return [];
        }

        return props.calculatedTableRows
            .filter((x) => x.isSelected)
            .map((x) =>
                demeterApi.runDairyInsurancePrices({
                    ...x,
                    ...props.calculationInputs,
                    asOfDate: props.calculationInputs.coverageEffectiveDate,
                    reinsuranceYear: props.calculationInputs.insuranceYear,
                    state: props.calculationInputs.insuranceState,
                }),
            );
    });

    useEffect(() => {
        refreshRunDairyInsurancePrices();
    }, [props.calculatedTableRows]);

    const buildCsv = (): DataSeries[] | undefined => {
        if (!props.calculatedTableRows || props.calculatedTableRows?.length === 0) {
            return [];
        }

        const newData = dairyPremiumInformationTableHeaders.map((row) => ({
            label: `${row.header ?? row.headerValueGetter!()}`,
            data: [
                ...props.calculatedTableRows.map((x, index) => ({
                    value: `${x[row.field as keyof DairyInsurancePriceModel]}`,
                    date: `${x.asOfDate}(${index + 1})`,
                    rawDate: new Date(x.asOfDate)!,
                    isActualValue: true,
                })),
            ],
        }));

        return newData;
    };

    const handleExportButtonClick = () => {
        const downloadData = buildCsv();
        tableCsvDownloadService.downloadCsv(downloadData!, translations.risk.insuranceQuoteEstimator.text.coverageLevels);
    };

    const getCombinedAverage = (classBasedValue: number, componentBasedValue: number) =>
        ((classBasedValue ?? 0) * props.calculationInputs.classBasePercent!) / 100 +
        ((componentBasedValue ?? 0) * props.calculationInputs.componentBasedPercent!) / 100;

    const tableRows = useMemo(() => {
        if (!runDairyInsurancePricesResponse || runDairyInsurancePricesResponse?.length === 0) {
            return [];
        }

        const newOptions: DairyPremiumInformationTableRow[][] = [];

        runDairyInsurancePricesResponse
            ?.filter((x) => x.rows?.length !== 0)
            .forEach((x) => {
                const classBasedRow = x.rows!.find((y) => y.commodity === DemeterCommodity.MilkClass)!;
                const componentBasedRow = x.rows!.find((y) => y.commodity === DemeterCommodity.MilkComponents)!;
                const combinedRow = x.rows!.find((y) => y.commodity === DemeterCommodity.Milk)!;

                // Class based.
                const classBasedExpectedYield = classBasedRow?.expectedYield;
                const classBasedRevenueGuarenteePerCwt = classBasedRow?.revenueGuarantyPerHundredWeight;
                const classBasedDeclaredMilkProduction = classBasedRow?.declaredMilkProduction;
                const classBasedRevenueGuarentee = classBasedRow?.revenueGuaranty;
                const classBasedPremiumBeforeSubsidy = classBasedRow?.totalPremium;
                const classBasedPremiumBeforeSubsidyCwt = classBasedRow?.totalPremiumPerHundredWeight;
                const classBasedSubsidy = classBasedRow?.subsidyPremium;
                const classBasedSubsidyPerCwt = classBasedRow?.subsidyPremiumPerHundredWeight;
                const classBasedPremiumAfterSubsidy = classBasedRow?.producerPremium;
                const classBasedPremiumAfterSubsidyPerCwt = classBasedRow?.producerPremiumPerHundredWeight;

                // Component based.
                const componentBasedExpectedYield = componentBasedRow?.expectedYield;
                const componentBasedRevenueGuarenteePerCwt = componentBasedRow?.revenueGuarantyPerHundredWeight;
                const componentBasedDeclaredMilkProduction = componentBasedRow?.declaredMilkProduction;
                const componentBasedRevenueGuarentee = componentBasedRow?.revenueGuaranty;
                const componentBasedPremiumBeforeSubsidy = componentBasedRow?.totalPremium;
                const componentBasedPremiumBeforeSubsidyCwt = componentBasedRow?.totalPremiumPerHundredWeight;
                const componentBasedSubsidy = componentBasedRow?.subsidyPremium;
                const componentBasedSubsidyPerCwt = componentBasedRow?.subsidyPremiumPerHundredWeight;
                const componentBasedPremiumAfterSubsidy = componentBasedRow?.producerPremium;
                const componentBasedPremiumAfterSubsidyPerCwt = componentBasedRow?.producerPremiumPerHundredWeight;

                // Combined based.
                const combinedExpectedYield = combinedRow?.expectedYield;
                const combinedRevenueGuarenteePerCwt = combinedRow?.revenueGuarantyPerHundredWeight;
                const combinedDeclaredMilkProduction = combinedRow?.declaredMilkProduction;
                const combinedRevenueGuarentee = combinedRow?.revenueGuaranty;
                const combinedPremiumBeforeSubsidy = combinedRow?.totalPremium;
                const combinedPremiumBeforeSubsidyCwt = combinedRow?.totalPremiumPerHundredWeight;
                const combinedSubsidy = combinedRow?.subsidyPremium;
                const combinedSubsidyPerCwt = combinedRow?.subsidyPremiumPerHundredWeight;
                const combinedPremiumAfterSubsidy = combinedRow?.producerPremium;
                const combinedPremiumAfterSubsidyPerCwt = combinedRow?.producerPremiumPerHundredWeight;
                const currentRows: DairyPremiumInformationTableRow[] = [];
                if (props.calculationInputs.classBasePercent) {
                    currentRows.push({
                        ...x,
                        endorsementType: translations.risk.headers.classBasedEndorsement,
                        expectedYield: classBasedExpectedYield,
                        revenueGuarenteePerCwt: classBasedRevenueGuarenteePerCwt,
                        declaredMilkProduction: classBasedDeclaredMilkProduction,
                        revenueGuarentee: classBasedRevenueGuarentee,
                        premiumBeforeSubsidy: classBasedPremiumBeforeSubsidy,
                        premiumBeforeSubsidyCwt: classBasedPremiumBeforeSubsidyCwt,
                        subsidy: classBasedSubsidy,
                        subsidyPerCwt: classBasedSubsidyPerCwt,
                        premiumAfterSubsidy: classBasedPremiumAfterSubsidy,
                        premiumAfterSubsidyPerCwt: classBasedPremiumAfterSubsidyPerCwt,
                    });
                }

                if (props.calculationInputs.componentBasedPercent) {
                    currentRows.push({
                        ...x,
                        endorsementType: translations.risk.headers.componentBasedEndorsement,
                        expectedYield: componentBasedExpectedYield,
                        revenueGuarenteePerCwt: componentBasedRevenueGuarenteePerCwt,
                        declaredMilkProduction: componentBasedDeclaredMilkProduction,
                        revenueGuarentee: componentBasedRevenueGuarentee,
                        premiumBeforeSubsidy: componentBasedPremiumBeforeSubsidy,
                        premiumBeforeSubsidyCwt: componentBasedPremiumBeforeSubsidyCwt!,
                        subsidy: componentBasedSubsidy,
                        subsidyPerCwt: componentBasedSubsidyPerCwt!,
                        premiumAfterSubsidy: componentBasedPremiumAfterSubsidy,
                        premiumAfterSubsidyPerCwt: componentBasedPremiumAfterSubsidyPerCwt!,
                    });
                }

                if (includeTotalsRow) {
                    currentRows.push({
                        ...x,
                        endorsementType: translations.risk.headers.combinedEndorsement,
                        expectedYield: combinedExpectedYield,
                        revenueGuarenteePerCwt: combinedRevenueGuarenteePerCwt,
                        declaredMilkProduction: combinedDeclaredMilkProduction,
                        revenueGuarentee: combinedRevenueGuarentee,
                        premiumBeforeSubsidy: combinedPremiumBeforeSubsidy,
                        premiumBeforeSubsidyCwt: combinedPremiumBeforeSubsidyCwt!,
                        subsidy: combinedSubsidy,
                        subsidyPerCwt: combinedSubsidyPerCwt!,
                        premiumAfterSubsidy: combinedPremiumAfterSubsidy,
                        premiumAfterSubsidyPerCwt: combinedPremiumAfterSubsidyPerCwt!,
                    } as DairyPremiumInformationTableRow);
                }

                newOptions.push(currentRows);
            });

        return newOptions;
    }, [runDairyInsurancePricesResponse]);

    return (
        <div className={styles.insurances_quote_estimator}>
            <div className={styles.liquidity_risk_section}>
                <PageAccordion
                    title={translations.risk.insuranceQuoteEstimator.headers.dairyRevenueProtectionPremiumInformation}
                    open={props.accordionOpen}
                    component={
                        <>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <div className={styles.liquidity_risk_blue_box}>
                                        <img src={informationSvg} alt="#" /> {translations.risk.insuranceQuoteEstimator.text.thisIsAQuoteOnly}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_right}>
                                    <CoverageLevelsActions
                                        calculationInputs={props.calculationInputs}
                                        subscriptionType={DemeterEmailSubscriptionType.DairyInsurancePriceSubscription}
                                        useDairyAgent
                                    />
                                </div>
                            </div>
                            {tableRows.length !== 0 &&
                                props.calculatedTableRows.filter((x) => x.isSelected).length === tableRows.length &&
                                props.calculatedTableRows
                                    .filter((x) => x.isSelected)
                                    .map((x, index) => (
                                        <div className={styles.liquidity_risk_gap}>
                                            <TableWrapper
                                                title={x.title}
                                                isLoading={false}
                                                headerOptions={{
                                                    isCsvDownloadAvailable: true,
                                                    handleExportButtonClick,
                                                }}
                                                track={{
                                                    eventCategory: 'InsuranceQuoteEstimator',
                                                    eventAction: 'DairyPremiumInformation',
                                                }}
                                            >
                                                <Table
                                                    tableHeaders={dairyPremiumInformationTableHeaders}
                                                    tableData={tableRows[index]}
                                                    includeTotalsRow={!!includeTotalsRow}
                                                />
                                            </TableWrapper>
                                        </div>
                                    ))}
                        </>
                    }
                />
            </div>
        </div>
    );
};

export default DairyRevenueProtectionPremiumInformation;
